import { fetchDataByOpportunityId } from 'api/customerFlow'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getStartDateRange } from 'features/registration/utils'
import { getPriceSimulation, storeSimulationParams } from 'features/simulation/utils'
import Router from 'next/router'
import { store } from 'store'
import { updateMarketingData } from 'store/marketing/actions/updateMarketingData'
import { MarketingDataKeys } from 'store/marketing/types'
import { updateAllRegistrationInputs } from 'store/registration/actions/updateAllRegistrationInputs'
import { Language } from 'types/language'
import { MarketingParams } from 'types/marketing-params'
import { routes } from './routes'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { CodeTypes } from 'types/promo-referral-codes'
import { SimulationInputKeys } from 'store/simulation/types'

dayjs.extend(isBetween)
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Fetches the data for the given opportunity id & push it into the store
 *
 * @param Language locale
 */
export const getDataByOpportunityId = async (locale: Language): Promise<void> => {
  const params = new URLSearchParams(location.search)

  if (params.has('opportunityId')) {
    // Clear window session storage if it exists
    if (window.sessionStorage) window.sessionStorage.clear()

    // Fetch the no EAN data
    const { confirmationRoundup, forcedAmount, producerSlug, simulationParams, promoReferralCode, inputs } = await fetchDataByOpportunityId(
      params.get('opportunityId') ?? ''
    )

    // Update the confirmation roundup marketing store parameter
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.CONFIRMATION_ROUNDUP, value: confirmationRoundup }))

    // Check if contract start date is still valid & change if necessary
    const { max, min, initial } = getStartDateRange(inputs.meterDetails.situation)

    if (!dayjs(inputs.meterDetails.contractStartDate).isBetween(min, max, 'day')) {
      inputs.meterDetails.contractStartDate = initial
    }

    // Set the registration store
    store.dispatch(updateAllRegistrationInputs({ inputs }))

    // Check if the simulation is not null & stores the values if so
    if (simulationParams) {
      await storeSimulationParams(simulationParams)

      // Save promo referral code to simulation store
      if (promoReferralCode && promoReferralCode.valid) {
        store.dispatch(
          updateSimulationInput({
            key: promoReferralCode.type === CodeTypes.PROMO ? SimulationInputKeys.PROMO_CODE : SimulationInputKeys.REFERRAL_CODE,
            value: promoReferralCode.type === CodeTypes.PROMO ? promoReferralCode.promoCode : promoReferralCode.referralCode
          })
        )
      }

      getPriceSimulation(forcedAmount)
    }

    // Check if the producerSlug is not null & redirect to registration if so
    if (producerSlug && producerSlug !== '') {
      window.sessionStorage.setItem('producerSlug', producerSlug)

      Router.push(routes(locale, producerSlug).registration)
    }
  } else {
    Router.push(routes(locale).producers)
  }
}

/**
 * Sets marketing params in Redux store
 *
 * @param marketingParams
 */
export const setMarketingParamsInStore = (marketingParams: MarketingParams): void => {
  const { sourceType, simulationSo } = marketingParams

  if (sourceType) store.dispatch(updateMarketingData({ key: MarketingDataKeys.SOURCE_TYPE, value: sourceType }))
  if (simulationSo) store.dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: simulationSo }))
}
